import Badge from "@components/Badge";
import {
  FilterAction,
  FilterActions,
  FilterKeys,
  VehicleFilter,
} from "@modules/vehicle/types/vehicleFilter";
import classNames from "classnames";
import { CloseIcon } from "@components/icons";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import Button from "@common/components/Button";
import getVehicleFilterDescription from "../helpers/getVehicleFilterDescription";
import canVehicleFilterBeBadged from "../helpers/canVehicleFilterBeBadged";

type ActiveBadge = {
  filterKey: FilterKeys;
  primaryText: string;
  mainKey?: string;
  subKey?: string;
};

const badgeTypeOrder = {
  [FilterKeys.Location]: 1,
  [FilterKeys.Province]: 2,
  [FilterKeys.Year]: 3,
  [FilterKeys.Make]: 4,
  [FilterKeys.Model]: 5,
  [FilterKeys.Mileage]: 6,
  [FilterKeys.Payment]: 7,
  [FilterKeys.Price]: 8,
  [FilterKeys.BodyStyle]: 9,
  [FilterKeys.FuelType]: 10,
  [FilterKeys.FuelEfficiency]: 11,
  [FilterKeys.ExteriorColour]: 12,
  [FilterKeys.KeyFeatures]: 13,
  [FilterKeys.Transmission]: 14,
  [FilterKeys.SavedVehicles]: 15,
  [FilterKeys.Search]: 16,
  [FilterKeys.SortBy]: 17,
  [FilterKeys.Warranty]: 18,
  [FilterKeys.PassengerCapacity]: 19,
};

const badgeSort = (a: ActiveBadge, b: ActiveBadge) => {
  if (a.filterKey && b.filterKey && a.filterKey === b.filterKey) {
    return a.primaryText?.localeCompare(b.primaryText);
  }

  return badgeTypeOrder[a.filterKey] - badgeTypeOrder[b.filterKey];
};

const toBadge = (vehicleFilter: VehicleFilter, locale: string): ActiveBadge => {
  const badgeText = getVehicleFilterDescription(vehicleFilter, locale);
  switch (vehicleFilter.key) {
    case FilterKeys.FuelEfficiency:
    case FilterKeys.Year:
    case FilterKeys.Mileage:
    case FilterKeys.SavedVehicles:
    case FilterKeys.Warranty:
    case FilterKeys.Search:
    case FilterKeys.Payment:
    case FilterKeys.Price:
      return {
        filterKey: vehicleFilter.key,
        primaryText: badgeText,
      };
    case FilterKeys.Model:
    case FilterKeys.KeyFeatures:
    case FilterKeys.Transmission:
    case FilterKeys.BodyStyle:
    case FilterKeys.ExteriorColour:
    case FilterKeys.FuelType:
    case FilterKeys.Location:
      return {
        filterKey: vehicleFilter.key,
        mainKey: vehicleFilter.state.id,
        primaryText: badgeText,
      };
    case FilterKeys.Make:
      return {
        filterKey: FilterKeys.Make,
        mainKey: vehicleFilter.state.make,
        primaryText: badgeText,
      };
    case FilterKeys.Province:
      return {
        filterKey: FilterKeys.Province,
        mainKey: vehicleFilter.state.id,
        primaryText: badgeText,
      };
    case FilterKeys.PassengerCapacity: {
      return {
        filterKey: FilterKeys.PassengerCapacity,
        mainKey: vehicleFilter.state.id,
        primaryText: badgeText,
      };
    }
    default:
      throw new Error("Missing badge conversion for FilterKey");
  }
};

const getBadges = (
  vehicleFilters: VehicleFilter[],
  locale: string
): ActiveBadge[] => {
  return vehicleFilters
    .filter((x) => canVehicleFilterBeBadged(x))
    .map((vf) => toBadge(vf, locale))
    .sort(badgeSort);
};

type FilterBadgesProps = {
  onFilterUpdate(filterAction: FilterAction): void;
  reverse?: boolean;
  scrollable?: boolean;
  vehicleFilters: VehicleFilter[];
};

const VehicleBadgeContainer = ({
  reverse = false,
  scrollable = false,
  vehicleFilters,
  onFilterUpdate,
}: FilterBadgesProps) => {
  const { t } = useTranslation(["common"]);
  const router = useRouter();
  const activeBadges = getBadges(vehicleFilters, router.locale || "");
  const filterText = `${activeBadges.length} ${
    activeBadges.length === 1 ? t("common:filter") : t("common:filters")
  } ${t("common:applied")}`;

  const removeAllBadges = () => {
    onFilterUpdate({
      type: FilterActions.ResetFilters,
      payload: undefined,
    });
  };

  const removeBadge = (activeBadge: ActiveBadge) => {
    switch (activeBadge.filterKey) {
      case FilterKeys.Year:
      case FilterKeys.Mileage:
      case FilterKeys.FuelEfficiency:
      case FilterKeys.Payment:
      case FilterKeys.Price:
        onFilterUpdate({
          type: FilterActions.RemoveFilter,
          payload: activeBadge.filterKey,
        });
        break;
      case FilterKeys.Make:
        onFilterUpdate({
          type: FilterActions.RemoveMakeFilter,
          payload: {
            make: activeBadge.mainKey ?? "",
          },
        });
        break;
      case FilterKeys.Model:
      case FilterKeys.KeyFeatures:
      case FilterKeys.Transmission:
      case FilterKeys.BodyStyle:
      case FilterKeys.ExteriorColour:
      case FilterKeys.FuelType:
      case FilterKeys.Location:
      case FilterKeys.Province:
      case FilterKeys.PassengerCapacity:
        onFilterUpdate({
          type: FilterActions.RemoveByIdAndType,
          payload: {
            id: activeBadge.mainKey || "",
            type: activeBadge.filterKey,
          },
        });
        break;
      case FilterKeys.SavedVehicles:
        onFilterUpdate({
          type: FilterActions.SetSavedVehiclesFilter,
          payload: false,
        });
        break;
      case FilterKeys.Warranty:
        onFilterUpdate({
          type: FilterActions.RemoveWarrantyFilter,
          payload: false,
        });
        break;
      case FilterKeys.Search:
        onFilterUpdate({
          type: FilterActions.RemoveSearchFilter,
          payload: undefined,
        });
        break;
      default:
    }
  };

  return (
    <div
      className={classNames(
        "bg-white lg:rounded-lg min-h-24 px-4 sm:px-6 py-2.5 flex border-b border-gray-50",
        {
          "flex-wrap ": activeBadges.length > 0 && !reverse,
          "flex-wrap-reverse": activeBadges.length > 0 && reverse,
          hidden: activeBadges.length === 0,
        }
      )}
      key="badge-container"
    >
      <div
        className={`w-full items-center [&>button]:py-0 [&>button]:px-2  flex ${
          reverse ? "justify-between pt-3" : "pb-3"
        }`}
      >
        <span className="body-3 italic text-text-light-200">{filterText}</span>
        <Button
          fill="link"
          className="hover:bg-transparent underline min-w-0 active:bg-transparent focus:outline-none focus-visible:shadow-elevation-01"
          onClick={removeAllBadges}
        >
          {t("common:clear_all")}
        </Button>
      </div>
      <div
        className={`flex gap-y-1.5 gap-x-1 flex-row ${
          scrollable ? "overflow-x-auto pb-4" : "flex-wrap"
        }`}
      >
        {activeBadges.map((activeBadge) => (
          <div
            className="flex-shrink-0 "
            key={activeBadge.filterKey + activeBadge.primaryText}
          >
            <Badge
              as="button"
              onRightIconClick={() => removeBadge(activeBadge)}
              rightIcon={<CloseIcon />}
              text={activeBadge.primaryText}
              textStyle="dark"
              badgeStyle="primary-alt"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default VehicleBadgeContainer;
